import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import { Container, Button, Typography, Paper, Box, Grid, CircularProgress, Alert, LinearProgress } from '@mui/material';
import { styled } from '@mui/material/styles';

axios.defaults.withCredentials = true;

const Dropzone = styled('div')(({ theme }) => ({
  border: `2px dashed ${theme.palette.divider}`,
  padding: theme.spacing(4),
  textAlign: 'center',
  cursor: 'pointer',
  borderRadius: theme.shape.borderRadius,
  transition: 'border-color 0.3s',
  '&:hover': {
    borderColor: theme.palette.primary.main,
  },
}));

const ImgPreview = styled('img')({
  width: '100px',
  height: '100px',
  objectFit: 'cover',
  borderRadius: '8px',
  margin: '8px',
});

function UploadImage() {
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const { raceId } = useParams();
  const [imagePreviews, setImagePreviews] = useState([]);
  const [raceName, setRaceName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    const fetchUserRoleAndRace = async () => {
      try {
        const userStatusResponse = await axios.get('/api/users/status');
        if (userStatusResponse.data.loggedIn) {
          setUserRole(userStatusResponse.data.user.role);
        } else {
          setError('You must be logged in to upload images.');
        }

        const raceResponse = await axios.get(`/api/races/${raceId}`);
        setRaceName(raceResponse.data.name);

      } catch (err) {
        setError('Error fetching page data: ' + (err.response?.data?.message || err.message));
      } finally {
        setLoading(false);
      }
    };
    fetchUserRoleAndRace();
  }, [raceId]);

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles(files);
    generatePreviews(files);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const files = Array.from(event.dataTransfer.files);
    setSelectedFiles(files);
    generatePreviews(files);
  };

  const generatePreviews = (files) => {
    const previews = files.map(file => URL.createObjectURL(file));
    setImagePreviews(previews);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setError('Please select one or more files first!');
      return;
    }

    const formData = new FormData();
    selectedFiles.forEach(file => {
      formData.append('images', file);
    });
    formData.append('raceId', raceId);

    setUploading(true);
    setUploadProgress(0);
    try {
      const response = await axios.post('/api/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        },
      });
      setMessage(response.data.message || 'Upload successful!');
      setSelectedFiles([]);
      setImagePreviews([]);
      setError('');
    } catch (err) {
      setError('Upload failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  if (!userRole || !['admin', 'director', 'photographer'].includes(userRole)) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Alert severity="error">Access Denied. You do not have permission to upload images.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Upload Images for {raceName || `Race ID: ${raceId}`}
          </Typography>
          <Button component={Link} to={`/races/${raceId}/images`} variant="outlined">
            View Images
          </Button>
        </Box>
        <input
          type="file"
          multiple
          onChange={handleFileChange}
          style={{ display: 'none' }}
          id="fileInput"
        />
        <label htmlFor="fileInput">
          <Dropzone onDragOver={handleDragOver} onDrop={handleDrop}>
            <Typography>Drag 'n' drop images here, or click to select them</Typography>
          </Dropzone>
        </label>

        {imagePreviews.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6">Selected Images:</Typography>
            <Grid container spacing={2}>
              {imagePreviews.map((preview, index) => (
                <Grid item key={index}>
                  <ImgPreview src={preview} alt={`preview ${index}`} />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpload}
            disabled={selectedFiles.length === 0 || uploading}
          >
            Upload
          </Button>
          {uploading && <CircularProgress size={24} sx={{ ml: 2 }} />}
        </Box>

        {uploading && (
          <Box sx={{ width: '100%', mt: 2 }}>
            <LinearProgress variant="determinate" value={uploadProgress} />
          </Box>
        )}

        {message && <Alert severity="success" sx={{ mt: 2 }}>{message}</Alert>}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </Paper>
    </Container>
  );
}

export default UploadImage;
